/**
 * API 호출은 same-origin `/v1/…`으로만 한다 — 내부 API 주소를 프런트가 모르게(README "노출 경계").
 * 버전이 경로 맨 앞이다. 웹에는 버전이 없고 버저닝은 API 계약에만 있다.
 * dev는 vite 프록시, prod는 console-web 산출물을 서빙하는 API가 넘긴다.
 *
 * 인증 헤더를 붙이지 않는다 — 로그인 세션은 httpOnly 쿠키라 브라우저가 알아서 싣는다(스크립트는 토큰을 못 본다).
 * 액세스 토큰(15분)이 끝나 401이 오면 리프레시 토큰으로 한 번 바꾸고 같은 요청을 다시 보낸다.
 */
async function requestJson<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const res = await fetch(`/v1${path}`, {
    ...init,
    headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}), ...init?.headers },
  });
  // 로그인 경로 자체의 401(비밀번호·코드 틀림)은 세션 만료가 아니다 — 교체하지 않고 사유를 그대로 띄운다.
  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    if (await ApiClient.refreshSession()) return requestJson<T>(path, init, true);
    LoginRedirect.go();
  }
  if (!res.ok) throw new Error(`요청 실패 (HTTP ${res.status}) — ${await reasonOf(res)}`);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** 세션이 끝났으면 지금 화면을 기억해 로그인 화면으로 보낸다. */
class LoginRedirect {
  static go(): void {
    if (window.location.pathname.startsWith('/login')) return;
    const next = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  }
}

/**
 * 실패 사유를 서버 본문에서 꺼내 쓴다 — 콘솔은 사내 도구라 "HTTP 400"만 띄우면 무엇이 잘못됐는지
 * 알아내려고 매번 개발자 도구를 열게 된다.
 *
 * **`message`를 먼저 본다.** Nest는 `new BadRequestException('사유')`를
 * `{ statusCode, message: '사유', error: 'Bad Request' }`로 감싸므로, `error`를 먼저 보면 실제 사유
 * 대신 'Bad Request'라는 영문 분류명이 화면에 뜬다. 사유를 `{ error }` 객체로 직접 싣는 응답은
 * `message`가 없으므로 그다음 갈래가 받는다.
 */
async function reasonOf(res: Response): Promise<string> {
  try {
    const payload: unknown = await res.json();
    if (typeof payload === 'object' && payload !== null) {
      const { error, message } = payload as { error?: unknown; message?: unknown };
      if (message !== undefined) return Array.isArray(message) ? message.join(', ') : String(message);
      if (typeof error === 'string' && error) return error;
    }
  } catch {
    // 본문이 JSON이 아니면 상태 코드만으로 충분하다.
  }
  return res.statusText;
}

/** 동시에 여러 요청이 401을 받아도 교체는 한 번만 한다 — 같은 리프레시 토큰으로 두 번 바꾸면 두 번째는 거절된다. */
let refreshing: Promise<boolean> | null = null;

export class ApiClient {
  static refreshSession(): Promise<boolean> {
    refreshing ??= fetch('/v1/auth/session/refresh', { method: 'POST' })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  }

  /** JSON이 아닌 응답(첨부 바이트 등)은 브라우저가 직접 부른다 — 그때도 접두어는 이 파일이 소유한다. */
  static assetUrl(path: string): string { return `/v1${path}`; }

  static getJson<T>(path: string): Promise<T> { return requestJson<T>(path); }
  static postJson<T>(path: string, body?: unknown): Promise<T> {
    return requestJson<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
  }
  static patchJson<T>(path: string, body: unknown): Promise<T> {
    return requestJson<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
  }
  /** 파일 바이트를 본문으로 그대로 올린다(멀티파트가 아니다). 이름·형식은 호출하는 쪽이 쿼리로 싣는다. */
  static postFile<T>(path: string, file: Blob): Promise<T> {
    return requestJson<T>(path, { method: 'POST', body: file, headers: { 'content-type': 'application/octet-stream' } });
  }
  static putJson<T>(path: string, body: unknown): Promise<T> {
    return requestJson<T>(path, { method: 'PUT', body: JSON.stringify(body) });
  }
  static deleteJson<T>(path: string): Promise<T> { return requestJson<T>(path, { method: 'DELETE' }); }
}
